/**
 * Fixed outgoing IP across the fleet: each client's proxy state, its last
 * health check and this month's charge, with Pause and Resume.
 *
 * The portal does not touch the proxy itself. The client access Lambda
 * (`fixedIp.status`, `fixedIp.pause`, `fixedIp.resume`) invokes the client's
 * own fixed-egress-probe Lambda with `{"portal": "status" | "pause" | "resume"}`,
 * so a pause from here goes through the same code as a pause in the client's
 * Settings, and the client's admins are told (lambdas/python/fixed-egress-probe,
 * documentation/network-egress). The probe records the person signed in as who
 * paused or resumed it.
 */
import type { Client } from '@/types';
import { activityService } from './activityService';
import { clientAccess } from './clientAccess';
import { mapWithConcurrency } from './announcementsService';

export type FixedIpState = 'active' | 'paused' | 'suspended' | 'resuming' | 'starting' | 'off';

export interface FixedIpStatus {
  enabled: boolean;
  ip?: string;
  region?: string;
  state?: FixedIpState;
  state_by?: string;
  state_at?: string;
  state_reason?: string;
  resume_at?: string;
  health?: {
    ok?: boolean;
    checked_at?: string;
    since?: string;
    latency_ms?: number;
    detail?: string;
  };
  month?: { month: string; credits: number; cost_usd: number; days_billed: number } | null;
}

/** One client's row: its status, or why it could not be read. */
export interface FixedIpRow {
  client: Client;
  status?: FixedIpStatus;
  error?: string;
}

/** The clients whose config builds a fixed IP. */
export const clientsWithFixedIp = (clients: Client[]): Client[] =>
  clients.filter((c) => (c.config as { fixedEgressIp?: boolean }).fixedEgressIp === true);

const invoke = async (
  client: Client,
  action: 'fixedIp.status' | 'fixedIp.pause' | 'fixedIp.resume',
  payload: Record<string, unknown> = {}
): Promise<FixedIpStatus> => {
  const { status } = await clientAccess<{ status: FixedIpStatus }>(action, { clientName: client.name, ...payload });
  return status;
};

/** The Lambda words each failure for the person reading the row. */
const describeError = (error: unknown): string => (error instanceof Error ? error.message : String(error));

export const fixedIpService = {
  async status(client: Client): Promise<FixedIpStatus> {
    return invoke(client, 'fixedIp.status');
  },

  /** Every client with the fixed IP, 8 at a time. A failure is kept on its row. */
  async fleet(clients: Client[]): Promise<FixedIpRow[]> {
    return mapWithConcurrency(clientsWithFixedIp(clients), 8, async (client) => {
      try {
        return { client, status: await invoke(client, 'fixedIp.status') };
      } catch (error) {
        return { client, error: describeError(error) };
      }
    });
  },

  /**
   * `hours` null: paused until somebody resumes it. `by` goes to the portal's activity log only:
   * the probe records the person signed in.
   */
  async pause(client: Client, by: string, hours: number | null, reason: string): Promise<FixedIpStatus> {
    try {
      const status = await invoke(client, 'fixedIp.pause', { reason, ...(hours ? { hours } : {}) });
      await activityService.logActivity({
        type: 'config',
        action: 'updated',
        resourceType: 'fixed-ip',
        resourceId: client.name,
        details: { metadata: { action: 'pause', by, hours, reason } },
        success: true,
      });
      return status;
    } catch (error) {
      throw new Error(describeError(error));
    }
  },

  async resume(client: Client, by: string): Promise<FixedIpStatus> {
    try {
      const status = await invoke(client, 'fixedIp.resume');
      await activityService.logActivity({
        type: 'config',
        action: 'updated',
        resourceType: 'fixed-ip',
        resourceId: client.name,
        details: { metadata: { action: 'resume', by } },
        success: true,
      });
      return status;
    } catch (error) {
      throw new Error(describeError(error));
    }
  },
};
